// src/components/layout/BlockOnboardingRerun.jsx
// Bouncer, mirror of RequireOnboarding: keeps a paying customer who has already
// finished onboarding from running it a second time.
//
// Why this exists: finishing onboarding on the "free" plan calls
// subscriptionService.initializeFreeTier(), which stamps subscriptionStatus:'free'
// over the user document. A customer who re-ran onboarding to change their exam
// therefore wiped their own paid plan — the client kept showing premium (it falls
// back to the subscriptions collection) while the backend refused every paid paper.
// Changing exam after onboarding is admin-only anyway, so re-running it has no
// legitimate use for a subscriber.
import React, { useEffect, useState } from 'react';
import { Outlet, Navigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import enhancedUserService from '../../services/enhancedUserService';
import { isSupportedLang } from '../../i18n';

const DEFAULT_LANG = 'en';

export default function BlockOnboardingRerun({ user, subscriptionStatus, isLoadingSubscription, isAdmin }) {
  const { t } = useTranslation();
  const { lang } = useParams();
  const currentLang = isSupportedLang(lang) ? lang : DEFAULT_LANG;
  const [checking, setChecking] = useState(true);
  const [onboardingComplete, setOnboardingComplete] = useState(false);

  // Deliberately NOT exempting ?resume=1. Checkout's four "skip" buttons all
  // navigate to /onboarding?resume=1&checkout=skipped, and OnboardingPage then
  // forces pricingPlanId to 'free' — which is precisely how a subscriber got
  // back into onboarding and downgraded himself. OnboardingPage's own bouncer
  // has the same hole (it returns early on `resume`).
  //
  // No exemption is needed: during a genuine first run saveOnboardingComplete
  // has not been called yet, so onboardingComplete is false and the check below
  // lets the user through on its own. Returning from checkout mid-signup keeps
  // working; only an already-completed subscriber is turned away.

  useEffect(() => {
    if (!user?.uid) {
      setChecking(false);
      setOnboardingComplete(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const data = await enhancedUserService.getOnboardingComplete(user.uid);
      if (cancelled) return;
      // Same completion signal RequireOnboarding uses, so the two guards can
      // never disagree and bounce a user back and forth.
      setOnboardingComplete(!!(data && (data.examName != null || data.placementLevel != null)));
      setChecking(false);
    })();
    return () => { cancelled = true; };
  }, [user?.uid]);

  // Admins change exam selection through onboarding; that is a supported flow.
  if (!user?.uid || isAdmin) return <Outlet />;

  if (checking || isLoadingSubscription) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-black/60">{t('onboarding.loading', 'Loading...')}</p>
      </div>
    );
  }

  const hasPremium = !!subscriptionStatus?.isActive && !subscriptionStatus?.isFree;
  if (hasPremium && onboardingComplete) {
    return <Navigate to={`/${currentLang}/dashboard`} replace />;
  }

  return <Outlet />;
}
